// ** react imports
import React, { useState, useRef, useEffect } from 'react';

// ** router imports
import { Link, useNavigate } from 'react-router-dom';

// ** redux imports
import { logout } from '../redux/slices/users';
import { useDispatch, useSelector } from 'react-redux';

// ** utils
import Transition from '../utils/Transition';
import { USER_ROLES } from '../enums/common';

function DropdownProfile({ align }) {
  // states
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // refs
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { users } = useSelector(state => state.users);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // handlers
  const handleLogout = () => {
    dispatch(logout());
    navigate('/auth/signin');
  };

  // avatar helpers
  const getInitials = name => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] || '' : '';
    return (first + last).toUpperCase() || '?';
  };

  const stringToColor = str => {
    if (!str) return '#6B7280'; // gray-500 fallback
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 55%)`;
  };

  return (
    <div className="relative inline-flex z-40">
      <button
        ref={trigger}
        className="inline-flex justify-center items-center group"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <div className="flex items-center truncate">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold mr-2 shrink-0"
            style={{ backgroundColor: stringToColor(users?.fullName || '') }}
            aria-hidden="true"
          >
            {getInitials(users?.fullName)}
          </div>
          <span className="truncate text-sm font-medium text-gray-600 dark:text-gray-100 group-hover:text-gray-800 dark:group-hover:text-white">
            {users?.fullName}
          </span>
          <svg
            className="w-3 h-3 shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500"
            viewBox="0 0 12 12"
          >
            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
          </svg>
        </div>
      </button>

      <Transition
        className={`origin-top-right absolute top-full z-40 min-w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${
          align === 'right' ? 'right-0' : 'left-0'
        } ${dropdownOpen ? 'block' : 'hidden'}`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="pt-0.5 pb-2 px-3 mb-1 border-b border-gray-200 dark:border-gray-700/60">
            <div className="font-medium text-gray-800 dark:text-gray-100">
              {users?.fullName}
            </div>
            <div className="uppercase text-xs text-gray-500 dark:text-gray-400 italic">
              {users?.role === USER_ROLES.ADMIN ? 'Administrator' : users?.role}
            </div>
          </div>
          <ul>
            <li>
              <Link
                to={`/users/details/${users?._id}?page=profile`}
                className="font-medium text-sm text-violet-500 hover:text-violet-600 dark:hover:text-violet-400 flex items-center py-1 px-3"
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                }}
              >
                Update Profile
              </Link>
            </li>
            <li>
              <Link
                className="font-medium text-sm text-violet-500 hover:text-violet-600 dark:hover:text-violet-400 flex items-center py-1 px-3"
                onClick={() => {
                  handleLogout();
                  setDropdownOpen(!dropdownOpen);
                }}
              >
                Sign Out
              </Link>
            </li>
          </ul>
        </div>
      </Transition>
    </div>
  );
}

export default DropdownProfile;
